import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

export function Card({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground rounded-3xl border border-border',
        // Resting elevation plus a light top edge. The edge is transparent in
        // the light theme and a faint white in the dark one, because a drop
        // shadow does almost nothing on a near black ground and lift has to
        // come from the surface catching light instead.
        'shadow-[var(--elevation-resting)] [box-shadow:inset_0_1px_0_0_var(--edge-highlight),var(--elevation-resting)]',
        // Interactive cards lift; static ones do not. Opt in with `group/card`
        // on the element that owns the hover, so a card full of links does not
        // flinch every time the cursor crosses it.
        'transition-[box-shadow,border-color,transform] duration-200 ease-out',
        'group-hover/card:[box-shadow:inset_0_1px_0_0_var(--edge-highlight),var(--elevation-lifted)]',
        'group-hover/card:border-border/80',
        'motion-reduce:transition-none',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn('flex flex-col gap-1 px-6 pt-6', className)}
      {...props}
    />
  );
}

export function CardTitle({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('text-sm font-semibold tracking-tight text-foreground', className)}
      {...props}
    />
  );
}

export function CardDescription({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

export function CardContent({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-6 py-5', className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 pb-6', className)}
      {...props}
    />
  );
}
